import { useEffect, useState, type FormEvent, type ReactNode } from "react";
import {
  Alert,
  ApiError,
  Button,
  Spinner,
  cn,
  type PartnerMeResponse,
  type PartnerMemberCreate,
  type PartnerMemberOut,
  type PartnerMemberRoleUpdate,
  type PartnerRole,
} from "@dearbill/shared";
import { useApi } from "../auth/useApi";
import { MemberRow } from "../components/MemberRow";
import { inviteErrorMessage, removeErrorMessage } from "./memberErrors";

/** The invite card's role choices, each with a one-line description of what that role sees. */
const ROLE_CHOICES: ReadonlyArray<{ value: PartnerRole; label: string; detail: string }> = [
  {
    value: "user",
    label: "Referrer",
    detail: "Can check costs and view only the patients they referred.",
  },
  {
    value: "org_admin",
    label: "Admin",
    detail: "Can check costs, view all referred patients and add or remove people.",
  },
];

/** The server's member order: the owner first, then by email. */
function byOwnerThenEmail(a: PartnerMemberOut, b: PartnerMemberOut): number {
  if (a.isOwner !== b.isOwner) return a.isOwner ? -1 : 1;
  return a.email.localeCompare(b.email);
}

/**
 * Team tab (`/orgs/:orgId/members`), rendered only for an `org_admin` caller.
 *
 * The shell hides the tab for other roles, and the endpoints 403 them too.
 * An invite carries email and role, plus an optional name and mobile number
 * (Figma dR8cJ6hRSW5eERToo64boe node 2340:653) that prefill the invitee's
 * setup form; the member picks their own password and OTP-confirms whatever
 * phone they keep at setup.
 *
 * The list holds the owner first, then the rest by email, as the server sends
 * it. The owner's row has no action buttons, since the owner's role is fixed.
 * Self-targeting is prevented in the UI: the caller's own row (matched by
 * `userId` from `GET /api/partner/me`) has both action buttons disabled. The
 * server's 403 owner guard and 400 self-target guard are backstops, not normal
 * paths.
 */
export function MembersTab({ orgId, orgName }: { orgId: string; orgName: string }): ReactNode {
  const api = useApi();

  const [members, setMembers] = useState<PartnerMemberOut[] | null>(null);
  const [currentUserId, setCurrentUserId] = useState<string | null>(null);
  const [loadError, setLoadError] = useState<string | null>(null);

  const [inviteName, setInviteName] = useState("");
  const [invitePhone, setInvitePhone] = useState("");
  const [email, setEmail] = useState("");
  // No preselected role: granting permissions is an explicit choice, so the
  // radios start unchecked and the submit stays disabled until one is picked.
  const [role, setRole] = useState<PartnerRole | null>(null);
  const [inviting, setInviting] = useState(false);
  const [inviteError, setInviteError] = useState<string | null>(null);
  const [invitedEmail, setInvitedEmail] = useState<string | null>(null);

  useEffect(() => {
    let cancelled = false;
    setMembers(null);
    setLoadError(null);
    Promise.all([
      api<PartnerMemberOut[]>(`/api/partner/orgs/${orgId}/members`),
      api<PartnerMeResponse>("/api/partner/me"),
    ])
      .then(([list, me]) => {
        if (cancelled) return;
        setMembers(list);
        setCurrentUserId(me.userId);
      })
      .catch((err: unknown) => {
        if (cancelled) return;
        // A 401 has cleared the session in `useApi`, and the redirect takes over.
        if (err instanceof ApiError && err.status === 401) return;
        setLoadError("We couldn't load this organization's members. Please try again.");
      });
    return () => {
      cancelled = true;
    };
  }, [api, orgId]);

  const onInvite = (e: FormEvent): void => {
    e.preventDefault();
    if (inviting || !canInvite || role === null) return;
    setInviting(true);
    setInviteError(null);
    setInvitedEmail(null);
    // Name and phone are optional-but-encouraged: they ride along only when
    // filled, and prefill the invitee's setup form server-side.
    const trimmedName = inviteName.trim();
    const trimmedPhone = invitePhone.trim();
    api<PartnerMemberOut>(`/api/partner/orgs/${orgId}/members`, {
      method: "POST",
      body: {
        ...(trimmedName !== "" ? { name: trimmedName } : {}),
        ...(trimmedPhone !== "" ? { phone: trimmedPhone } : {}),
        email: trimmedEmail,
        role,
      } satisfies PartnerMemberCreate,
    })
      .then((created) => {
        setInviting(false);
        setInvitedEmail(created.email);
        // Reset to the untouched form: next invite starts with no role decided.
        setInviteName("");
        setInvitePhone("");
        setEmail("");
        setRole(null);
        setMembers((prev) => {
          if (!prev) return [created];
          // A re-invite resurrects an existing row, so replace it rather than duplicate it.
          const without = prev.filter((m) => m.userId !== created.userId);
          return [...without, created].sort(byOwnerThenEmail);
        });
      })
      .catch((err: unknown) => {
        setInviting(false);
        if (err instanceof ApiError && err.status === 401) return;
        setInviteError(inviteErrorMessage(err));
      });
  };

  const changeRole = async (userId: string, next: PartnerRole): Promise<void> => {
    // The server response is authoritative; swap the returned member into the list.
    const updated = await api<PartnerMemberOut>(`/api/partner/orgs/${orgId}/members/${userId}`, {
      method: "PATCH",
      body: { role: next } satisfies PartnerMemberRoleUpdate,
    });
    setMembers((prev) => (prev ? prev.map((m) => (m.userId === userId ? updated : m)) : prev));
  };

  const removeMember = (member: PartnerMemberOut): void => {
    const label = member.name ?? member.email;
    if (!window.confirm(`Remove ${label} from this organization? They can be re-invited later.`)) {
      return;
    }
    api(`/api/partner/orgs/${orgId}/members/${member.userId}`, { method: "DELETE" })
      .then(() => {
        setMembers((prev) => (prev ? prev.filter((m) => m.userId !== member.userId) : prev));
      })
      .catch((err: unknown) => {
        if (err instanceof ApiError && err.status === 401) return;
        // Surface remove failures at the section level (the row has no slot for it).
        setLoadError(removeErrorMessage(err));
      });
  };

  // The lenient email shape of the enrollment schema's `_EMAIL_RE`.
  const trimmedEmail = email.trim();
  const emailValid = /\S+@\S+\.\S+/.test(trimmedEmail);
  // Block re-inviting someone already in the table, case-insensitively.
  // Removed members aren't in the list, so they stay invitable. The server's
  // 409 is the backstop.
  const alreadyMember =
    members?.some((m) => m.email.toLowerCase() === trimmedEmail.toLowerCase()) ?? false;
  const canInvite = emailValid && !alreadyMember && role !== null;
  // Only nag about an existing member once the typed email is otherwise valid.
  const showAlreadyMemberHint = emailValid && alreadyMember;

  return (
    <div className="flex flex-col items-start gap-6 lg:flex-row">
      <div className="min-w-0 flex-1">
        {/* An h2, because the shell's org header holds the page's h1 (the org name). */}
        <h2 className="text-foreground font-serif text-[34px] font-semibold leading-[42px]">
          Team
        </h2>
        <p className="text-foreground mt-3 text-[17px] leading-6">
          {orgName} team members with DearBill access.
        </p>

        {loadError && (
          <Alert tone="error" title={loadError} className="mt-4">
            Reload the page to try again. If it keeps happening, use &ldquo;Need help? Call&rdquo;
            at the top of the page.
          </Alert>
        )}

        {members === null && !loadError && (
          <div className="text-slate-teal mt-6 flex items-center gap-3">
            <Spinner /> <span>Loading members…</span>
          </div>
        )}

        {members !== null && members.length === 0 && (
          <div className="border-border-strong mt-[18px] rounded-lg border bg-white px-6 py-10 text-center">
            <p className="text-foreground font-semibold">No members yet.</p>
            <p className="text-slate-teal mt-1">
              Invite a colleague with the &ldquo;Add a team member&rdquo; card.
            </p>
          </div>
        )}

        {members !== null && members.length > 0 && (
          <div className="border-border-strong mt-[18px] overflow-x-auto rounded-xl border bg-white">
            <table className="w-full text-left">
              {/* The dark header band of the design's Team table. There is no
                  "Last signed in" column: the member wire carries no sign-in
                  stamp. */}
              <thead className="bg-deep-navy">
                <tr className="text-[13px] font-semibold uppercase leading-[18px] tracking-[0.8px] text-white">
                  <th className="py-4 pl-6 pr-4 font-semibold">Name</th>
                  <th className="px-4 py-4 font-semibold">Role</th>
                  <th className="py-4 pl-4 pr-6 text-right font-semibold">Actions</th>
                </tr>
              </thead>
              <tbody>
                {members.map((m) => (
                  <MemberRow
                    key={m.userId}
                    member={m}
                    isSelf={m.userId === currentUserId}
                    onRoleChange={(next) => changeRole(m.userId, next)}
                    onRemove={() => removeMember(m)}
                  />
                ))}
              </tbody>
            </table>
          </div>
        )}
      </div>

      <section className="border-border-strong w-full shrink-0 rounded-2xl border bg-white p-6 lg:w-[456px]">
        <h2 className="text-foreground font-serif text-2xl font-semibold leading-[30px]">
          Add a team member
        </h2>
        <form className="mt-3.5 flex flex-col gap-3.5" onSubmit={onInvite} noValidate>
          {/* Name and mobile are optional-but-encouraged (the design's order:
              Name, Mobile number, Email, Role). Both prefill the invitee's
              setup form, where they can change either. */}
          <div>
            <label
              htmlFor="invite-name"
              className="text-foreground block text-base font-semibold leading-[22px]"
            >
              Name
            </label>
            <input
              id="invite-name"
              type="text"
              autoComplete="off"
              placeholder="First and last name"
              value={inviteName}
              disabled={inviting}
              onChange={(e) => setInviteName(e.target.value)}
              className="border-haze text-foreground placeholder:text-slate-teal mt-1.5 block min-h-[52px] w-full rounded-lg border bg-white px-4 py-[13px] text-lg leading-6 disabled:cursor-not-allowed disabled:opacity-60"
            />
          </div>

          <div>
            <label
              htmlFor="invite-phone"
              className="text-foreground block text-base font-semibold leading-[22px]"
            >
              Mobile number
            </label>
            <input
              id="invite-phone"
              type="tel"
              autoComplete="off"
              placeholder="(000)000-0000"
              value={invitePhone}
              disabled={inviting}
              onChange={(e) => {
                setInvitePhone(e.target.value);
                setInviteError(null);
              }}
              className="border-haze text-foreground placeholder:text-slate-teal mt-1.5 block min-h-[52px] w-full rounded-lg border bg-white px-4 py-[13px] font-mono text-lg leading-6 disabled:cursor-not-allowed disabled:opacity-60"
            />
            <ul className="text-slate-teal mt-1.5 list-disc pl-5 text-sm leading-5">
              <li>We will text a code each time they sign in</li>
            </ul>
          </div>

          <div>
            <label
              htmlFor="invite-email"
              className="text-foreground block text-base font-semibold leading-[22px]"
            >
              Email
            </label>
            <input
              id="invite-email"
              type="email"
              required
              placeholder="name@pharmacy.com"
              value={email}
              disabled={inviting}
              onChange={(e) => setEmail(e.target.value)}
              className="border-haze text-foreground placeholder:text-slate-teal mt-1.5 block min-h-[52px] w-full rounded-lg border bg-white px-4 py-[13px] text-lg leading-6 disabled:cursor-not-allowed disabled:opacity-60"
            />
            <p className="text-slate-teal mt-1.5 text-sm leading-5">
              We email the invitation here. They pick their own password and confirm their phone.
            </p>
          </div>

          <fieldset>
            <legend className="text-foreground text-base font-semibold leading-[22px]">Role</legend>
            <div className="mt-2 flex flex-col gap-2">
              {ROLE_CHOICES.map((choice) => (
                <label
                  key={choice.value}
                  className={cn(
                    "flex cursor-pointer items-start gap-3.5 rounded-lg border bg-white px-4 py-3.5",
                    role === choice.value ? "border-ember" : "border-haze",
                    inviting && "cursor-not-allowed opacity-60",
                  )}
                >
                  {/* appearance-none with explicit borders, as in the shared
                      RadioGroup, because native radios render differently per
                      browser and OS; on Safari an unchecked radio can look
                      prefilled. */}
                  <input
                    type="radio"
                    name="invite-role"
                    value={choice.value}
                    checked={role === choice.value}
                    disabled={inviting}
                    onChange={() => setRole(choice.value)}
                    className="border-cloud-blue checked:border-ember mt-0.5 size-[22px] shrink-0 cursor-pointer appearance-none rounded-full border-2 bg-white transition-colors checked:border-[7px]"
                  />
                  <span className="min-w-0">
                    <span className="text-foreground block text-[17px] font-semibold leading-6">
                      {choice.label}
                    </span>
                    <span className="text-slate-teal block text-[15px] leading-[22px]">
                      {choice.detail}
                    </span>
                  </span>
                </label>
              ))}
            </div>
          </fieldset>

          <Button type="submit" variant="secondary" size="lg" disabled={inviting || !canInvite}>
            {inviting ? "Sending…" : "Send invitation"}
          </Button>

          {/* Say why the button is disabled: a missing or invalid email or an
              unchosen role gets one how-to line, and a duplicate gets its own
              hint below. */}
          {(!emailValid || role === null) && (
            <p className="text-slate-teal text-sm leading-5">
              {!emailValid && role === null
                ? "Enter their email and choose a role to send the invitation."
                : !emailValid
                  ? "Enter their email to send an invitation."
                  : "Choose a role to send the invitation."}
            </p>
          )}
          {showAlreadyMemberHint && (
            <p className="text-slate-teal text-sm leading-5">
              This person is already a member of this organization.
            </p>
          )}
          {inviteError && <Alert tone="error" title={inviteError} />}
          {invitedEmail && (
            <p
              role="status"
              className="border-success-border bg-success-bg text-success rounded-lg border px-4 py-3 text-sm"
            >
              Invitation email requested for {invitedEmail}.
            </p>
          )}
        </form>
      </section>
    </div>
  );
}
